import toast from 'react-hot-toast';
import {
  UnblockUserMutation,
  useGetBlockedUsersQuery,
  useUnblockUserMutation,
} from '../../../../generated/graphql';
import { EmptyBlockedUsers } from './EmptyBlockedUsers';
import { Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { BlockedUserItem } from './BlockedUsersItem';
import Spinner from '@client/components/Spinner';
import { useAsyncResult } from '@client/new/helpers/gql';
import { ActionModal } from './ActionModal';

type BlockedUsersProps = {
  communityId: number;
};

export const BlockedUsers = ({ communityId }: BlockedUsersProps) => {
  const [unBlockUserId, setUnblockUserId] = useState<number | null>(null);
  const { data, loading, refetch } = useGetBlockedUsersQuery({
    variables: { communityId },
    fetchPolicy: 'network-only',
  });
  const hasBlockedUsers = (data?.blockedUsers ?? []).length > 0;
  const [unblockUserMutation, unblockUserStateRaw] = useUnblockUserMutation();
  const unblockUserState =
    useAsyncResult<UnblockUserMutation>(unblockUserStateRaw);
  const isSubmitting = unblockUserState.type === 'fetching';

  useEffect(() => {
    if (
      unblockUserState.type === 'fetched' &&
      unblockUserState?.value?.unblockUser
    ) {
      refetch();
      setUnblockUserId(null);
    }
    if (unblockUserState.type === 'fetched' && !unblockUserState?.value) {
      toast.error('Something went wrong with unblocking user');
      setUnblockUserId(null);
    }
  }, [unblockUserState, refetch]);

  const onUnblock = async (communityId: number, userId: number) =>
    unblockUserMutation({
      variables: {
        communityId,
        userId,
      },
    });

  const onDismiss = () => setUnblockUserId(null);

  return (
    <>
      {loading ? (
        <Box className="w-full flex justify-center items-center">
          <Spinner />
        </Box>
      ) : hasBlockedUsers ? (
        <Box className="flex flex-col gap-2.5 w-[690px]">
          {(data?.blockedUsers || []).map((user) => (
            <BlockedUserItem
              user={user}
              key={user.id}
              onUnblock={() => setUnblockUserId(user.id)}
            />
          ))}
        </Box>
      ) : (
        <EmptyBlockedUsers />
      )}
      <ActionModal
        title="Are you sure you want to unblock this user?"
        onDismiss={onDismiss}
        onSubmit={() => unBlockUserId && onUnblock(communityId, unBlockUserId)}
        text="This means that they will be able to post again in your community."
        actionText="Unblock user"
        isModalVisible={!!unBlockUserId}
        isSubmitting={isSubmitting}
      />
    </>
  );
};
